import React from 'react';
import {
  Stack, Box, Typography, FormControl, InputLabel, Select, MenuItem, TextField,
} from '@mui/material';

/**
 * Shared column→role mapping UI for pipeline registration and editing.
 *
 * Two rendering modes over the same `assignments` state
 * ({ [column]: { role, taxonomy_rank?, kind? } }):
 *   - per-column table (the caller renders this) for kinds with a multi-use
 *     role (e.g. Taxa, where many columns take the taxonomy role at different
 *     ranks);
 *   - DirectRolePickers (here) for kinds whose roles are all single-use
 *     (e.g. Classifications), where the natural question is "which column is
 *     the X?" rather than "what is each column's role?".
 *
 * isSimpleRoleKind decides which mode applies.
 */

// A kind qualifies for direct per-role pickers when none of its roles is
// multi-use. Such kinds map at most one column per role, so the inverted
// "role → column" picker reads more naturally than a per-column grid.
export function isSimpleRoleKind(roles) {
  return Array.isArray(roles) && roles.length > 0 && roles.every((r) => !r.multi);
}

// Sub-choice field for a role that requires extra annotation (taxonomy_rank
// dropdown, or a free-text `kind`). Shared by both the per-column table and
// the direct pickers.
export function SubChoice({ role, assignment, onChange }) {
  if (!role) return null;
  const requires = role.requires || [];
  return (
    <Stack direction="row" spacing={1}>
      {requires.includes('taxonomy_rank') && (
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Rank</InputLabel>
          <Select
            value={assignment.taxonomy_rank || ''}
            label="Rank"
            onChange={(e) => onChange({ taxonomy_rank: e.target.value })}
          >
            {(role.sub_choices?.taxonomy_rank || []).map((rank) => (
              <MenuItem key={rank} value={rank}>{rank}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {requires.includes('kind') && (
        <TextField
          size="small"
          label="kind"
          value={assignment.kind || ''}
          onChange={(e) => onChange({ kind: e.target.value })}
          sx={{ minWidth: 140 }}
          helperText="e.g. read_count, tpm, similarity"
        />
      )}
    </Stack>
  );
}

/**
 * Direct per-role column pickers for simple-role kinds.
 *
 * Reads and writes the same `assignments` map the per-column table uses, so
 * downstream inference / preview / submit logic is untouched — this is just a
 * friendlier input surface for the single-use-role case.
 *
 * @param {string[]} columns          all available column names
 * @param {Array}    roles            the kind's registration_roles
 * @param {Object}   assignments      { [column]: { role, taxonomy_rank?, kind? } }
 * @param {Function} setAssignments   state setter (accepts updater fn)
 * @param {string}   idColumn         the specimen-identifier column (excluded from role choices)
 * @param {Function|null} onChangeIdColumn  setter for the ID column; null = fixed (no ID picker)
 */
export function DirectRolePickers({
  columns, roles, assignments, setAssignments, idColumn, onChangeIdColumn,
}) {
  const columnForRole = (roleKey) =>
    Object.keys(assignments).find((c) => assignments[c] && assignments[c].role === roleKey) || '';

  // Move a single-use role onto `column` (clearing whichever column held it).
  const assignRole = (roleKey, column) => {
    setAssignments((prev) => {
      const next = {};
      for (const [c, a] of Object.entries(prev)) {
        if (a && a.role === roleKey) continue; // drop the previous holder → unmapped
        next[c] = a;
      }
      if (column) next[column] = { ...(prev[column] || {}), role: roleKey };
      return next;
    });
  };

  const patchSubChoice = (column, patch) => {
    setAssignments((prev) => ({ ...prev, [column]: { ...(prev[column] || {}), ...patch } }));
  };

  const roleChoices = columns.filter((c) => c !== idColumn);

  return (
    <Stack spacing={2}>
      {onChangeIdColumn && (
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Which column is the specimen identifier?</InputLabel>
          <Select
            label="Which column is the specimen identifier?"
            value={idColumn || ''}
            onChange={(e) => onChangeIdColumn(e.target.value)}
          >
            {columns.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      )}
      {roles.map((role) => {
        const held = columnForRole(role.key);
        // If the held column became the ID column, show the picker as unset
        // (the stale assignment is filtered out of submission anyway).
        const value = held === idColumn ? '' : held;
        return (
          <Box key={role.key}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel>{`Which column is the ${role.label}?`}</InputLabel>
                <Select
                  label={`Which column is the ${role.label}?`}
                  value={value}
                  onChange={(e) => assignRole(role.key, e.target.value)}
                  displayEmpty
                >
                  {!role.required && <MenuItem value=""><em>(none)</em></MenuItem>}
                  {roleChoices.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              {value && (
                <SubChoice
                  role={role}
                  assignment={assignments[value] || {}}
                  onChange={(patch) => patchSubChoice(value, patch)}
                />
              )}
            </Stack>
            {role.description && (
              <Typography variant="caption" color="text.secondary">{role.description}</Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
